import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ============ DATE/TIME FORMATTING ============

/**
 * Format date for display (e.g., "Mon, Jan 15")
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    // Append T12:00:00 to date-only strings to avoid UTC midnight timezone shift
    const normalized = dateStr.length === 10 ? dateStr + 'T12:00:00' : dateStr;
    const date = new Date(normalized);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format date short (e.g., "Jan 15")
 */
export const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  try {
    // Append T12:00:00 to date-only strings to avoid UTC midnight timezone shift
    const normalized = dateStr.length === 10 ? dateStr + 'T12:00:00' : dateStr;
    const date = new Date(normalized);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format ISO date string to readable format
 */
export const formatISODate = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return isoString;
  }
};

/**
 * Format time duration in hours and minutes
 */
export const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Format time for display (e.g., "2:30 PM")
 */
export const formatTime = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

// ============ PHONE FORMATTING ============

/**
 * Format phone number for display (e.g., "(813) 555-1234")
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

/**
 * Format phone number for input (e.g., "(813) 555-1234")
 */
export const formatPhoneInput = (value) => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6,10)}`;
};

/**
 * Clean phone number (remove all non-digits)
 */
export const cleanPhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

// ============ NAVIGATION HELPERS ============

/**
 * Open Google Maps navigation to address
 */
export const openNavigation = (address) => {
  if (!address) return;
  const encodedAddress = encodeURIComponent(address);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    window.open(`maps://maps.apple.com/?daddr=${encodedAddress}`, '_blank');
  } else {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  }
};

/**
 * Open navigation to full route with waypoints
 */
export const openRouteNavigation = (addresses) => {
  if (!addresses || addresses.length === 0) return;
  
  const destination = encodeURIComponent(addresses[addresses.length - 1]);
  const waypoints = addresses.slice(0, -1).map(a => encodeURIComponent(a)).join('|');
  
  let url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
  if (waypoints) {
    url += `&waypoints=${waypoints}`;
  }
  
  window.open(url, '_blank');
};

/**
 * Initiate phone call
 */
export const callPhone = (phone) => {
  if (!phone) return;
  const cleaned = phone.replace(/\D/g, '');
  window.open(`tel:${cleaned}`, '_self');
};

// ============ CURRENCY FORMATTING ============

/**
 * Format currency for display
 */
export const formatCurrency = (amount, showCents = true) => {
  if (amount === null || amount === undefined) return '-';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '-';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  }).format(num);
};

// ============ STRING HELPERS ============

/**
 * Truncate string with ellipsis
 */
export const truncate = (str, maxLength = 50) => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + '...';
};

/**
 * Capitalize first letter of each word
 */
export const titleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

// ============ STATUS HELPERS ============

/**
 * Get status color class for jobs
 */
export const getStatusColor = (status) => {
  const statusColors = {
    'completed': 'text-green-500',
    'complete': 'text-green-500',
    'non_complete': 'text-red-500',
    'back_to_shop': 'text-blue-400',
    'in_progress': 'text-orange-500',
    'pending': 'text-zinc-300',
    'scheduled': 'text-blue-500'
  };
  return statusColors[status?.toLowerCase()] || 'text-zinc-300';
};

/**
 * Get status background color class
 */
export const getStatusBgColor = (status) => {
  const statusColors = {
    'completed': 'bg-green-500/10 border-green-500/20',
    'complete': 'bg-green-500/10 border-green-500/20',
    'non_complete': 'bg-red-500/10 border-red-500/20',
    'back_to_shop': 'bg-blue-500/10 border-blue-500/20',
    'in_progress': 'bg-orange-500/10 border-orange-500/20',
    'pending': 'bg-gray-500/10 border-gray-500/20',
    'scheduled': 'bg-blue-500/10 border-blue-500/20'
  };
  return statusColors[status?.toLowerCase()] || 'bg-gray-500/10 border-gray-500/20';
};

// ============ USER ROLE HELPERS ============

/**
 * Check if a user is assignable as a tech/driver.
 * Includes users with primary role driver OR secondary roles service/driver.
 * Excludes inactive users and shop-only technicians.
 */
export const isAssignableUser = (user) => {
  if (user.active === false) return false;
  const role = user.role || '';
  const secondaryRoles = user.secondary_roles || [];
  return role === 'driver' || role === 'technician' || secondaryRoles.includes('service') || secondaryRoles.includes('driver');
};

// iter r12p — Jake: autofilled phones sometimes carry "+1" that he has to
// delete by hand. Strip the US country code at autofill time.
export const stripUSCode = (phone) => {
  let s = String(phone || "").trim();
  if (!s) return "";
  if (s.startsWith("+1")) s = s.slice(2).trim();
  else if (s.replace(/\D/g, "").length === 11 && s.replace(/\D/g, "").startsWith("1")) {
    s = s.replace(/^\s*1[\s\-.]*/, "");
  }
  return s.trim();
};
